// ─── La griglia: fasce − chiusure − prenotazioni ─────────────────────────────
//
// 🔒 Modulo PURO: non conosce il database, non fa chiamate, non guarda
// l'orologio. Gli si danno le fasce, le chiusure e le prenotazioni di un
// giorno — tutte **in ora locale**, come le manda `GET /calendar` — e risponde
// dove si può prenotare.
//
// ⭐⭐ ESISTE PERCHÉ LA GRIGLIA SI CALCOLA IN UN POSTO SOLO. La disegna il
// browser, la interroga `GET /availability`, la fa rispettare il servizio che
// scrive: tre posti che la calcolassero per conto proprio sarebbero tre griglie
// che si somigliano, e la differenza si scoprirebbe da un rifiuto che a schermo
// sembrava impossibile.
//
// ⚖️ E NON È LA GUARDIA. Contro la doppia prenotazione difende **il vincolo di
// esclusione del database**, che due richieste simultanee non scavalcano (§ 01.5).
// Quello che questo modulo fa è dirlo **prima e in italiano**, così il rifiuto
// arriva con un motivo invece che con un errore di Postgres. Chi togliesse il
// vincolo fidandosi di qui avrebbe tolto l'unica difesa vera.
import { giornoDellaData, primoGiornoChiuso, ultimoGiornoChiuso, } from './circolo.js';
import { alleOre, minutiDaOra, oraDaMinuti, } from './calendario.js';
const OK = { ok: true };
/** Quanti giorni ci sono fra due date `YYYY-MM-DD`. Puro: nessun fuso di mezzo. */
export function giorniFra(da, a) {
    return Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${da}T00:00:00Z`)) / 86_400_000);
}
/** Quanti giorni copre la validità di una fascia: `Infinity` se non ha estremi. */
function ampiezzaValidita(f) {
    if (f.validFrom === null || f.validTo === null)
        return Number.POSITIVE_INFINITY;
    return giorniFra(f.validFrom, f.validTo);
}
/**
 * Le fasce che valgono per QUEL campo in QUEL giorno.
 *
 * ⭐⭐ DUE REGOLE DI SOSTITUZIONE, e sono la stessa idea dello strato del
 * listino: ciò che è più mirato **sostituisce** ciò che è generale, non ci
 * convive accanto.
 *
 * ① **il campo**: se esiste anche una sola fascia dedicata a questo campo, le
 *    fasce «tutti i campi» spariscono per lui. Un campo con un orario suo ha un
 *    orario suo, non due;
 * ② **il periodo**: fra quelle rimaste vince lo strato di validità più stretto.
 *    Una griglia «Estate» dichiarata copre quella di sempre finché dura.
 *
 * ⛔ L'alternativa — sommarle tutte — produce due passi diversi per la stessa
 * ora, e allora la griglia dipende da quale riga si guarda per prima. 📌 *Due
 * griglie sovrapposte non sono una griglia più ricca: sono una griglia che non
 * si sa disegnare.*
 */
export function fasceDelGiorno(fasce, date, courtId) {
    const giorno = giornoDellaData(date);
    if (giorno === null)
        return [];
    const applicabili = fasce.filter((f) => f.active &&
        f.weekday === giorno &&
        (f.courtId === null || f.courtId === courtId) &&
        (f.validFrom === null || date >= f.validFrom) &&
        (f.validTo === null || date <= f.validTo));
    if (applicabili.length === 0)
        return [];
    const dedicate = applicabili.filter((f) => f.courtId !== null);
    const inGara = dedicate.length > 0 ? dedicate : applicabili;
    const piuStretto = Math.min(...inGara.map(ampiezzaValidita));
    return inGara
        .filter((f) => ampiezzaValidita(f) === piuStretto)
        .sort((a, b) => a.startsAtTime.localeCompare(b.startsAtTime));
}
/**
 * Gli orari in cui si può COMINCIARE, in quel giorno su quel campo.
 *
 * ⚠️ Un passo entra solo se ci sta **intero** dentro la sua fascia: alle 22:30
 * di una griglia da novanta che chiude alle 23:00 non si comincia niente, e
 * mostrare quella cella vorrebbe dire offrire un click che il server rifiuta.
 */
export function passiDelGiorno(fasce, date, courtId) {
    const out = [];
    const visti = new Set();
    for (const f of fasceDelGiorno(fasce, date, courtId)) {
        const fine = minutiDaOra(f.endsAtTime);
        for (let t = minutiDaOra(f.startsAtTime); t + f.slotMinutes <= fine; t += f.slotMinutes) {
            const ora = oraDaMinuti(t);
            if (visti.has(ora))
                continue;
            visti.add(ora);
            out.push({ time: ora, slotMinutes: f.slotMinutes });
        }
    }
    return out.sort((a, b) => a.time.localeCompare(b.time));
}
/** Gli intervalli aperti del giorno, uniti: `[[540, 1380]]` per 09:00-23:00. */
export function intervalliDelGiorno(fasce, date, courtId) {
    const pezzi = fasceDelGiorno(fasce, date, courtId)
        .map((f) => [minutiDaOra(f.startsAtTime), minutiDaOra(f.endsAtTime)])
        .sort((a, b) => a[0] - b[0]);
    const uniti = [];
    for (const [da, a] of pezzi) {
        const ultimo = uniti[uniti.length - 1];
        // `da <= ultimo[1]`: due fasce che si toccano (09:00-13:00 e 13:00-23:00)
        // sono un orario continuo, e una partita lunga può attraversarne il confine.
        if (ultimo && da <= ultimo[1])
            ultimo[1] = Math.max(ultimo[1], a);
        else
            uniti.push([da, a]);
    }
    return uniti;
}
/**
 * ⭐⭐ LE TRATTE: come `intervalliDelGiorno`, ma due bande si uniscono SOLO SE
 * HANNO LO STESSO PASSO. Ogni tratta porta con sé il suo passo.
 *
 * 🗣️ Nasce da lui, il 18/09/2026, dopo avere guardato la prova sul banco:
 * *«facciamo che io decido che quella banda è da 90 minuti: il socio può
 * prenotare solamente quella banda; io invece dal gestionale come segreteria
 * posso andarci sopra»*.
 *
 * 🚨 **IL DIFETTO CHE CURA, misurato prima di scriverla.** Banda libera
 * 12:30–15:30 col passo da 30, e accanto una banda a slot fissi 15:30–18:00 col
 * passo da 90. Una partita delle 14:30 da novanta minuti finisce alle 16:00:
 * `intervalliDelGiorno` le fondeva in una sola tirata 12:30–18:00, quindi quella
 * partita **passava** e si mangiava l'unico inizio della banda da 90.
 * ⇒ Lo slot delle 15:30 restava disegnato verde sul calendario, si apriva, e
 *   moriva soltanto sul bottone: *«Campo 1 alle 14:30 è già occupato»*.
 * 📌 *Una banda a slot fissi che chiunque può attraversare non è una banda a
 *    slot fissi: è una decorazione.*
 *
 * ⚖️ **E LE BANDE CON LO STESSO PASSO CONTINUANO A FONDERSI**, che è la metà da
 * non perdere: il lunedì vero del circolo ha 09:30–11:00 e 11:00–12:30 tutt'e
 * due da novanta — una sola tirata spezzata in due righe, non due regole
 * diverse. Chi mettesse il muro fra TUTTE le bande romperebbe quello.
 *
 * ⛔ Vale per il SOCIO. La segreteria passa da `dentroLOrarioDelCircolo`, che di
 * fasce non ne guarda nessuna: il muro non la riguarda, ed è esattamente ciò
 * che lui ha chiesto.
 */
export function tratteDelGiorno(fasce, date, courtId) {
    const pezzi = fasceDelGiorno(fasce, date, courtId)
        .map((f) => [minutiDaOra(f.startsAtTime), minutiDaOra(f.endsAtTime), f.slotMinutes])
        .sort((a, b) => a[0] - b[0]);
    const uniti = [];
    for (const [da, a, passo] of pezzi) {
        const ultimo = uniti[uniti.length - 1];
        if (ultimo && ultimo[2] === passo && da <= ultimo[1])
            ultimo[1] = Math.max(ultimo[1], a);
        else
            uniti.push([da, a, passo]);
    }
    return uniti;
}
/**
 * Regola ① del § 02.2: l'ora richiesta cade dentro una fascia prenotabile
 * attiva per quel campo e quel giorno, e **rispetta il passo**.
 *
 * ⚖️ IL PASSO GOVERNA DOVE SI COMINCIA, NON QUANTO SI STA — ed è una scelta,
 * dichiarata qui perché non si veda come una dimenticanza. Una lezione di
 * un'ora dentro una griglia da novanta minuti è normale e si deve poter fare;
 * una partita che comincia alle 09:07 no, perché scompagina tutte le celle
 * dopo di lei e non c'è nessuna schermata che sappia disegnarla.
 * ⇒ L'inizio deve essere un passo della griglia; la durata è libera, purché si
 *   finisca dentro l'orario di apertura.
 */
export function dentroLaGriglia(fasce, date, courtId, time, durationMinutes) {
    const passi = passiDelGiorno(fasce, date, courtId);
    if (passi.length === 0) {
        return {
            ok: false,
            codice: 'FUORI_ORARIO',
            messaggio: 'Quel giorno quel campo è riservato: se ti va, scegli un altro campo o un altro giorno.',
        };
    }
    const inizio = minutiDaOra(time);
    if (!passi.some((p) => p.time === time)) {
        const vicini = passi
            .map((p) => p.time)
            .filter((t) => Math.abs(minutiDaOra(t) - inizio) <= 180)
            .slice(0, 6);
        const suggerimento = vicini.length > 0
            ? ` Lì intorno si comincia alle ${vicini.join(', ')}.`
            : ' Se ti va, scegli un’altra ora.';
        return {
            ok: false,
            codice: 'FUORI_PASSO',
            messaggio: `Su quel campo le partite cominciano a orari fissi.${suggerimento}`,
        };
    }
    const fine = inizio + durationMinutes;
    const tratte = tratteDelGiorno(fasce, date, courtId);
    const tratta = tratte.find(([da, a]) => inizio >= da && inizio < a);
    if (!tratta || fine > tratta[1]) {
        const bordo = tratta?.[1] ?? 0;
        /*
         * ⚠️ IL RIFIUTO DICE COSA LO FERMA, e sono due cose diverse: una fascia che
         * finisce, oppure un'altra fascia che comincia con un altro passo. Dire
         * «chiude» nel secondo caso manderebbe a cercare un orario del circolo che
         * non c'entra niente — e qui c'era scritto proprio «quel campo quel giorno
         * chiude», che era falso già prima del muro: a finire è la fascia, non il
         * circolo.
         */
        const dopo = tratte.find(([da]) => da === bordo);
        const perche = dopo === undefined
            ? `oltre l’ultimo orario prenotabile`
            : `e ${alleOre(bordo)} comincia un’altra fascia, dove si entra ogni ${dopo[2]} minuti`;
        return {
            ok: false,
            codice: 'OLTRE_LA_FASCIA',
            messaggio: `Una prenotazione di ${durationMinutes} minuti dalle ${time} finirebbe ` +
                `${alleOre(fine)}, ${perche}. Se ti va, scegli un’ora un po’ prima.`,
        };
    }
    return OK;
}
/**
 * ⭐⭐ LA STESSA DOMANDA, MA FATTA DALLA SEGRETERIA: sta dentro l'ORARIO DEL
 * CIRCOLO?
 *
 * ⚖️ **Perché sono due funzioni e non un parametro di quella sopra.** Una sola
 * funzione con un interruttore `chiPrenota` si chiama allo stesso modo nei due
 * casi, e allora l'unica cosa che separa il socio dalla segreteria è un
 * argomento che si può dimenticare. Due nomi diversi si leggono dal punto in cui
 * si chiamano, e chi legge la rotta vede subito **per conto di chi** sta
 * decidendo (⇒ `regole.ts`).
 *
 * ⛔ E NON CONTROLLA IL PASSO, di proposito. Il passo è la griglia proposta ai
 * soci; qui non c'è nessuna griglia, c'è un campo libero dentro un circolo
 * aperto. ⇒ La segreteria può mettere una partita alle 10:15 se le serve, e può
 * trascinarne una di un quarto d'ora.
 * ⚠️ A tenere gli inizi in ordine è la SCHERMATA, che offre i passi buoni; a
 * tenere fuori il doppio campo resta il vincolo del database, che non è cambiato.
 */
export function dentroLOrarioDelCircolo(orario, time, durationMinutes) {
    const apre = minutiDaOra(orario.opensAt);
    const chiude = minutiDaOra(orario.closesAt);
    const inizio = minutiDaOra(time);
    const fine = inizio + durationMinutes;
    if (inizio < apre) {
        return {
            ok: false,
            codice: 'PRIMA_DELL_APERTURA',
            messaggio: `Il circolo apre alle ${orario.opensAt}, e quella prenotazione comincerebbe alle ${time}.`,
        };
    }
    if (fine > chiude) {
        return {
            ok: false,
            codice: 'DOPO_LA_CHIUSURA',
            messaggio: `Una prenotazione di ${durationMinutes} minuti dalle ${time} finirebbe ` +
                `${alleOre(fine)}, e il circolo chiude ${alleOre(chiude)}.`,
        };
    }
    return OK;
}
/**
 * Gli intervalli del giorno in cui il circolo è aperto ma **i soci non possono
 * prenotare**: l'orario del circolo meno le fasce di quel campo.
 *
 * 🎨 È ciò che il calendario disegna in grigio pieno, ed è anche l'elenco delle
 * ore su cui la segreteria può cliccare lo stesso.
 *
 * ⚠️ Può tornare vuoto in due casi opposti, e il chiamante non deve confonderli:
 * un giorno **tutto coperto** dalle fasce, e un giorno in cui le fasce
 * traboccano oltre l'orario del circolo. Il secondo non è un guasto — una
 * fascia più larga dell'orario è una fascia scritta prima che l'orario
 * esistesse — e qui si ritaglia senza protestare.
 */
export function intervalliFuoriFascia(fasce, orario, date, courtId) {
    const apre = minutiDaOra(orario.opensAt);
    const chiude = minutiDaOra(orario.closesAt);
    const fuori = [];
    let cursore = apre;
    for (const [da, a] of intervalliDelGiorno(fasce, date, courtId)) {
        // Una fascia che comincia prima dell'apertura non apre il circolo prima:
        // sposta solo il cursore, e il pezzo «prima» non nasce affatto.
        if (da > cursore)
            fuori.push([cursore, Math.min(da, chiude)]);
        cursore = Math.max(cursore, a);
        if (cursore >= chiude)
            break;
    }
    if (cursore < chiude)
        fuori.push([cursore, chiude]);
    return fuori.filter(([da, a]) => a > da);
}
/**
 * Gli orari in cui la SEGRETERIA può cominciare una prenotazione fuori fascia.
 *
 * ⚖️ Il passo è quello della prima fascia di quel campo in quel giorno — così
 * le celle grigie si allineano con quelle verdi invece di formare una seconda
 * griglia sfalsata — e in mancanza di fasce sono trenta minuti, che è il passo
 * più comune e l'unico che non ha bisogno di essere spiegato.
 */
export function passiFuoriFascia(fasce, orario, date, courtId) {
    const delGiorno = fasceDelGiorno(fasce, date, courtId);
    const passo = delGiorno[0]?.slotMinutes ?? 30;
    const out = [];
    for (const [da, a] of intervalliFuoriFascia(fasce, orario, date, courtId)) {
        /*
         * ⭐ Si parte dall'APERTURA e non dall'inizio del pezzo, poi si scartano i
         * passi che cadono prima: così i due pezzi di uno stesso giorno — quello
         * del mattino e quello della sera — stanno sulla STESSA cadenza. Partendo
         * dal pezzo, un buco che comincia alle 16:00 con passo 90 darebbe inizi a
         * 16:00 e 17:30, mentre il mattino ne darebbe a 08:00 e 09:30: due griglie
         * nello stesso giorno, sullo stesso campo.
         */
        for (let t = minutiDaOra(orario.opensAt); t + passo <= a; t += passo) {
            if (t < da)
                continue;
            out.push({ time: oraDaMinuti(t), slotMinutes: passo });
        }
    }
    return out;
}
/** Regola ②: la chiusura che tocca quell'intervallo, se c'è. */
export function chiusuraCheTocca(chiusure, courtId, date, daMinuto, aMinuto) {
    return (chiusure.find((c) => c.date === date &&
        (c.courtId === null || c.courtId === courtId) &&
        daMinuto < c.toMinute &&
        c.fromMinute < aMinuto) ?? null);
}
/**
 * Regola ③, **detta prima**: la prenotazione confermata che occupa già quel
 * pezzo di campo.
 *
 * ⛔ Non è la guardia — la guardia è `bookings_senza_sovrapposizioni` nel
 * database (§ 01.5). Questa serve a due cose che il vincolo non sa fare:
 * disegnare le celle occupate, e dire **chi** c'è già invece di dire che
 * qualcosa è andato storto.
 */
export function prenotazioneCheTocca(prenotazioni, courtId, date, daMinuto, aMinuto, escludiId) {
    return (prenotazioni.find((p) => {
        if (p.status !== 'confirmed')
            return false;
        if (p.courtId !== courtId)
            return false;
        if (p.id === escludiId)
            return false;
        // Sull'asse del giorno chiesto: una prenotazione del giorno prima che
        // sconfinasse oltre la mezzanotte entrerebbe con un offset negativo.
        const inizio = giorniFra(date, p.date) * 1440 + minutiDaOra(p.time);
        return daMinuto < inizio + p.durationMinutes && inizio < aMinuto;
    }) ?? null);
}
/**
 * 🎾 **Il maestro è già su un ALTRO campo, a quell'ora?** (voce 9, Q3,
 * 27/09/2026, scelta A). Torna la prenotazione che lo tiene occupato, o `null`.
 *
 * ⚖️ Lo stesso campo resta fuori di proposito: lì a dire «occupato» è già
 *    `prenotazioneCheTocca`, e il database lo difende. Questa risponde all'altra
 *    domanda — *dove sta lui* — che nessun vincolo sui campi sa fare.
 * La usano i due lati con due esiti diversi: il maestro non se la vede offrire;
 * la segreteria legge un avviso, e può salvare lo stesso.
 */
export function maestroGiaAltrove(prenotazioni, instructorId, courtId, date, daMinuto, aMinuto, escludiId) {
    return (prenotazioni.find((p) => {
        if (p.status !== 'confirmed')
            return false;
        if (p.instructorId !== instructorId)
            return false;
        if (p.courtId === courtId)
            return false;
        if (p.id === escludiId)
            return false;
        const inizio = giorniFra(date, p.date) * 1440 + minutiDaOra(p.time);
        return daMinuto < inizio + p.durationMinutes && inizio < aMinuto;
    }) ?? null);
}
/**
 * 🎾 **Il socio gioca già, a quell'ora, in un'ALTRA prenotazione?** (21/10/2026,
 * mockup «Socio alla stessa ora», scelta A). 🗣️ Dal bot sono comparse due
 * partite di giovedì alle 19:30 con lo stesso socio: dal bot non si può (una
 * partita alla volta), ma la Segreteria lo poteva mettere in campo due volte
 * senza accorgersene. Torna la prenotazione che lo tiene già, o `null`.
 *
 * ⚖️ Come per il maestro (`maestroGiaAltrove`), per la Segreteria è un
 *    **avviso**, non un blocco: spostando un socio da un campo all'altro lo si
 *    mette nel nuovo e poi lo si toglie dal vecchio. Si guarda sulle
 *    prenotazioni del giorno già in memoria; quella che si sta guardando resta
 *    fuori (`escludiId`), e un ospite (senza socio) non conta.
 */
export function socioGiaAltrove(prenotazioni, memberId, date, daMinuto, aMinuto, escludiId) {
    return (prenotazioni.find((p) => {
        if (p.status !== 'confirmed')
            return false;
        if (p.id === escludiId)
            return false;
        if (!p.players.some((g) => g.memberId === memberId))
            return false;
        const inizio = giorniFra(date, p.date) * 1440 + minutiDaOra(p.time);
        return daMinuto < inizio + p.durationMinutes && inizio < aMinuto;
    }) ?? null);
}
/**
 * ⭐⭐ QUANTO SPAZIO C'È DAVVERO A PARTIRE DA QUELL'ORA, su quel campo.
 *
 * 🗣️ Nasce da un difetto visto da lui il 18/09/2026: *il calendario mostra
 * verde uno slot già occupato*, e lo si scopre solo premendo «Crea».
 *
 * 📏 **La causa, misurata e non dedotta**: la griglia disegna le celle con
 * `passiDelGiorno`, che conosce **solo le fasce**; il socio passa da
 * `slotLiberi`, che sottrae chiusure e partite. ⇒ Due elenchi nati da due
 * funzioni, e a una mancava un ingrediente.
 * 📌 *Il difetto non era nel disegno: era che la mano che disegna non sapeva
 *    ciò che la mano che prenota sapeva già.*
 *
 * Torna i minuti liberi da `daMinuto` fino al primo ostacolo — una chiusura o
 * una prenotazione confermata — e **zero** se quel minuto è già occupato.
 *
 * ⚖️ **Non si ferma al passo né alla fascia**, e non è una svista: la
 * segreteria può prenotare oltre la fascia (⇒ `dentroLOrarioDelCircolo`),
 * quindi la domanda a cui questa risponde è *quanto ce n'è*, non *quanto ne
 * spetta*. A tagliare è chi chiama.
 */
export function spazioLibero(chiusure, prenotazioni, date, courtId, daMinuto) {
    let limite = 1440;
    /** `true` se l'ostacolo copre proprio quel minuto; se no, avvicina il tetto. */
    const copre = (da, a) => {
        if (da <= daMinuto && daMinuto < a)
            return true;
        if (da > daMinuto && da < limite)
            limite = da;
        return false;
    };
    for (const c of chiusure) {
        if (c.date !== date)
            continue;
        if (c.courtId !== null && c.courtId !== courtId)
            continue;
        if (copre(c.fromMinute, c.toMinute))
            return 0;
    }
    for (const p of prenotazioni) {
        if (p.status !== 'confirmed')
            continue;
        if (p.courtId !== courtId)
            continue;
        // Come in `prenotazioneCheTocca`: una partita del giorno prima che
        // sconfini oltre la mezzanotte entra con un offset negativo.
        const inizio = giorniFra(date, p.date) * 1440 + minutiDaOra(p.time);
        if (copre(inizio, inizio + p.durationMinutes))
            return 0;
    }
    return limite - daMinuto;
}
/**
 * Gli slot liberi di un campo in un giorno, per una durata data.
 *
 * È ciò che risponde `GET /availability`: quello che il socio vede dal bot.
 *
 * 🔄🚨 **E QUI C'ERA SCRITTO CHE ANCHE LA GRIGLIA PASSA DI QUI: non era vero, e
 * la riga vecchia è CORRETTA, non affiancata** (18/09/2026). Diceva *«è anche
 * ciò che la griglia usa per sapere quali celle si possono cliccare: una sola
 * funzione, due usi»* — la griglia chiama `passiDelGiorno`, e le prenotazioni
 * non le guardava affatto. ⇒ I due elenchi restano **due**, e non possono
 * diventare uno: qui la durata è **data**, là non esiste ancora perché la
 * segreteria non l'ha scelta. A essere in comune è `spazioLibero`.
 * 📌 *Un commento che dichiara un'unità che il codice non ha è peggio di
 *    nessun commento: chi lo legge smette di cercare la differenza.*
 */
export function slotLiberi(fasce, chiusure, prenotazioni, date, courtId, durationMinutes) {
    return passiDelGiorno(fasce, date, courtId).filter((p) => {
        if (!dentroLaGriglia(fasce, date, courtId, p.time, durationMinutes).ok)
            return false;
        const da = minutiDaOra(p.time);
        const a = da + durationMinutes;
        if (chiusuraCheTocca(chiusure, courtId, date, da, a))
            return false;
        return prenotazioneCheTocca(prenotazioni, courtId, date, da, a) === null;
    });
}
/**
 * Una chiusura «di ogni anno» srotolata la sa fare solo chi conosce i fusi, e
 * quello è Postgres (⇒ `calendario.deposito.ts`). Qui resta la domanda più
 * corta: **questa riga riguarda quel giorno?**
 *
 * Serve alle schermate delle Impostazioni, che mostrano le chiusure com'è
 * scritto nel database e non ritagliate per giorno.
 *
 * 🔄🚨 **CORRETTA IL 17/09/2026, e sbagliava DUE VOLTE nello stesso verso.**
 * Qui c'era `c.startsAt.slice(0, 10)` e `c.endsAt.slice(0, 10)`:
 * · la fine è un **confine** e veniva contata come giorno chiuso ⇒ per una
 *   chiusura «dal 25 al 27» rispondeva `true` anche per il **28**, mentre il
 *   calendario quel giorno lo lascia aperto;
 * · e i primi dieci caratteri di `2026-12-24T23:00:00.000Z` sono il giorno di
 *   **Greenwich**, non quello del circolo ⇒ l'inizio cadeva un giorno indietro
 *   proprio per le chiusure scritte dall'Italia.
 * ⇒ I due giorni li dà adesso `primoGiornoChiuso` / `ultimoGiornoChiuso`
 *   (⇒ `circolo.ts`), che sono la **stessa** definizione usata dalla schermata
 *   delle Chiusure. Il difetto nasceva proprio da lì: la stessa domanda con due
 *   risposte scritte in due posti.
 *
 * 📌 *Non lo vedeva nessuno perché non la chiamava nessuno: una funzione
 * esportata e mai usata non è codice morto, è una trappola che aspetta.*
 */
export function chiusuraRiguardaIlGiorno(c, date) {
    const da = primoGiornoChiuso(c);
    const al = ultimoGiornoChiuso(c);
    if (!c.everyYear)
        return date >= da && date <= al;
    /* Di una ricorrente contano mese e giorno, l'anno no. Il confronto regge
       perché una ricorrente non può scavalcare il capodanno: lo vieta lo schema e
       lo rifiuta `court_closures_ricorrente_stesso_anno_check`. */
    const mmdd = date.slice(5, 10);
    return mmdd >= da.slice(5, 10) && mmdd <= al.slice(5, 10);
}
//# sourceMappingURL=griglia.js.map